import proj4 from 'proj4';
import {
  BufferGeometry,
  Color,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
  Vector3,
  WebGLRenderer,
} from 'three';
import { Texture } from 'three';

import { Levels } from '../../../../typings/api/skymap/rest/v0/.common';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { WebApi } from '../../services/web-api';
import { GeodataStoreFactory } from '../../stores/geodata-store-factory/geodata-store-factory';
import { isValidNumber } from '../../utils/math/number';
import { AssetManager } from '../asset-manager';
import { CoordinateManager } from '../coordinate-manager';
import { CogSource } from '../geodata/cog-asset/cog-source';
import { COG_MASK_TEXTURE } from '../geodata/cog-asset/cog-tile-loader';
import { createCogOrthoAsset } from '../geodata/cog-asset/create-cog-ortho-asset';
import { FlatPlaneWorldMapping } from '../geodata/ortho-tile-asset/flat-plane-world-mapping';
import { makeTileBoundsToWorldQuad } from '../geodata/ortho-tile-asset/make-tile-bounds-to-world-quad';
import { OrthoTileAsset } from '../geodata/ortho-tile-asset/ortho-tile-asset';
import { TileCache } from '../geodata/ortho-tile-asset/tile-cache';
import { TileLoader } from '../geodata/ortho-tile-asset/tile-loader';
import { InputHandler } from '../input-handler';
import { renderOrders } from '../render-orders';
import {
  MouseButtonType,
  MouseInputData,
} from '../utilities/input-event/input-event-data-extractor';
import { clamp } from '../utilities/math-helpers';
import { Tool } from './tool';

export async function parseWorldFile(url: string) {
  const [pixelWidth, _, __, pixelHeight, topLeftX, topLeftY] = (
    await WebApi.fetchS3DataAsText(url)
  ).split('\n');

  if (
    !isValidNumber(pixelWidth) ||
    !isValidNumber(pixelHeight) ||
    !isValidNumber(topLeftX) ||
    !isValidNumber(topLeftY) ||
    Number(pixelWidth) === 0 ||
    Number(pixelHeight) === 0
  ) {
    throw new Error('Invalid world file.');
  }

  return {
    pixelWidth: Math.abs(Number(pixelWidth)),
    pixelHeight: Math.abs(Number(pixelHeight)),
    topLeftX: Number(topLeftX),
    topLeftY: Number(topLeftY),
  };
}

type CompareOrthophotoAsset = {
  asset: OrthoTileAsset;
  geodataId: string;
  index: string;
};

export class OrthophotoComparisonTool extends Tool {
  private _enabled = false;
  leftQuad: CompareOrthophotoAsset | undefined;
  rightQuad: CompareOrthophotoAsset | undefined;
  divider: Mesh<BufferGeometry, ShaderMaterial> = new Mesh();
  isMouseDown = false;
  private renderer: WebGLRenderer | undefined;
  private leftRequestToken = 0;
  private rightRequestToken = 0;

  _dividerThreshold = 0.5;
  get dividerThreshold() {
    return this._dividerThreshold;
  }
  set dividerThreshold(value: number) {
    this._dividerThreshold = clamp(value, 0, 1);
    this.updateTileMaterials();
    this.divider.material.uniforms.divider.value = this._dividerThreshold;
  }

  _resolution = window.innerWidth;
  set resolution(value: number) {
    this._resolution = value;
    this.updateTileMaterials();
    this.divider.material.uniforms.resolution.value = this._resolution;
  }

  constructor(inputHandler: InputHandler) {
    super('orthophoto-comparison', inputHandler);
    this.visible = false;
    this.createDividerLine();
  }

  enabled(): boolean {
    return this._enabled;
  }

  enable() {
    this._enabled = true;
    this.visible = true;
    AssetManager.instance.addTemp(this.divider);
  }

  disable() {
    this._enabled = false;
    this.visible = false;
    this.removeAsset(this.leftQuad?.asset, true);
    this.removeAsset(this.rightQuad?.asset, true);
    this.leftQuad = undefined;
    this.rightQuad = undefined;
    // Invalidate request tokens to prevent stale requests from completing
    this.leftRequestToken++;
    this.rightRequestToken++;
    AssetManager.instance.removeTemp(this.divider);
  }

  createDividerLine() {
    const material = new ShaderMaterial({
      uniforms: {
        divider: { value: this._dividerThreshold },
        color: { value: new Color(0xfed530) },
        resolution: { value: this._resolution },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position.xy, 0.0, 1.0);
        }
      `,
      fragmentShader: `
        uniform float divider;
        uniform float resolution;
        uniform vec3 color;
        varying vec2 vUv;

        void main() {
          if (int(gl_FragCoord.x) != int(resolution * divider)) discard;
          gl_FragColor = vec4(color, 1.0);
        }
      `,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });

    const geometry = new PlaneGeometry(2, 2);
    this.divider = new Mesh(geometry, material);
    this.divider.frustumCulled = false;
    this.divider.renderOrder = renderOrders.compareOrthophotosDividerLine;
  }

  removeAsset(asset: OrthoTileAsset | undefined, dispose: boolean) {
    if (asset) {
      if (dispose) {
        // Cancels tile requests and frees cached textures that aren't on a tile mesh
        asset.dispose();
      }
      AssetManager.instance.removeTemp(asset, { dispose });
    }
  }

  private updateTileMaterials() {
    const updateAssetMaterials = (asset: OrthoTileAsset | undefined, isLeft: boolean) => {
      if (!asset) {
        return;
      }
      asset.traverse((child) => {
        if (child instanceof Mesh && child.material instanceof ShaderMaterial) {
          child.material.uniforms.divider.value = this._dividerThreshold;
          child.material.uniforms.resolution.value = this._resolution;
          child.material.uniforms.isLeft.value = isLeft;
        }
      });
    };
    updateAssetMaterials(this.leftQuad?.asset, true);
    updateAssetMaterials(this.rightQuad?.asset, false);
  }

  onMouseDown(mouseInputData: MouseInputData): boolean {
    if (mouseInputData.button.type === MouseButtonType.RightButton) {
      this.dividerThreshold = mouseInputData.pointer.client.x / this._resolution;
      this.isMouseDown = true;
    }
    return true;
  }

  onMouseUp(mouseInputData: MouseInputData) {
    if (mouseInputData.button.type === MouseButtonType.RightButton) {
      this.isMouseDown = false;
    }
    return Promise.resolve(true);
  }

  onMouseMove(mouseInputData: MouseInputData): boolean {
    if (this.isMouseDown) {
      this.dividerThreshold = mouseInputData.pointer.client.x / this._resolution;
    }

    return true;
  }

  onProjectProjectionUpdated() {
    // OrthoTileAsset handles projection updates internally through worldMapping
    return true;
  }

  private createDividerMaterial(texture: Texture, isLeft: boolean): ShaderMaterial {
    // COG tiles carry their transparency mask separately, in the green channel
    const mask = texture.userData[COG_MASK_TEXTURE] as Texture | undefined;
    return new ShaderMaterial({
      uniforms: {
        map: { value: texture },
        alphaMask: { value: mask ?? null },
        hasAlphaMask: { value: !!mask },
        divider: { value: this.dividerThreshold },
        isLeft: { value: isLeft },
        resolution: { value: this._resolution },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D map;
        uniform sampler2D alphaMask;
        uniform bool hasAlphaMask;
        uniform float divider;
        uniform bool isLeft;
        uniform float resolution;
        varying vec2 vUv;

        void main() {
          float screenX = gl_FragCoord.x / resolution;
          if ((isLeft && screenX > divider) || (!isLeft && screenX < divider)) discard;
          gl_FragColor = texture2D(map, vUv);
          if (hasAlphaMask) gl_FragColor.a *= texture2D(alphaMask, vUv).g;
          #include <colorspace_fragment>
        }
      `,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
  }

  async setLeftSide(id: string, index: string) {
    // Increment token to invalidate any in-flight requests
    this.leftRequestToken++;
    const requestToken = this.leftRequestToken;

    this.removeAsset(this.leftQuad?.asset, true);

    const asset = await this.createOrthoTileAsset(id, true);

    // Check if this request is still valid (token matches and tool is enabled)
    if (requestToken !== this.leftRequestToken || !this._enabled) {
      return;
    }

    // Only add to AssetManager if the request is still valid
    AssetManager.instance.addTemp(asset);

    this.leftQuad = {
      asset,
      geodataId: id,
      index: index,
    };
  }

  async setRightSide(id: string, index: string) {
    // Increment token to invalidate any in-flight requests
    this.rightRequestToken++;
    const requestToken = this.rightRequestToken;

    this.removeAsset(this.rightQuad?.asset, true);

    const asset = await this.createOrthoTileAsset(id, false);

    // Check if this request is still valid (token matches and tool is enabled)
    if (requestToken !== this.rightRequestToken || !this._enabled) {
      return;
    }

    // Only add to AssetManager if the request is still valid
    AssetManager.instance.addTemp(asset);

    this.rightQuad = {
      asset,
      geodataId: id,
      index: index,
    };
  }

  private async createOrthoTileAsset(id: string, isLeft: boolean) {
    const orthotile = await SkyMapAxiosServiceFactory.instance
      .createOrthoTileServiceV0()
      .fetchById({
        body: { id },
      });

    const assetId = `${isLeft ? 'left' : 'right'}-${id}`;
    const projectProjection = CoordinateManager.getInstance().getProjectProjection();
    const origin = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3(0, 0, 0);
    const materialGenerator = (texture: Texture) => this.createDividerMaterial(texture, isLeft);

    let asset: OrthoTileAsset;
    if (orthotile.data.format === 'COG') {
      const url = await GeodataStoreFactory.instance.createOrthoTileCogUrl(id);
      asset = createCogOrthoAsset({
        id: assetId,
        source: await CogSource.open(url),
        projectProjection,
        sceneOriginProject: origin, // anchor so numbers stay small
        material: { renderOrder: renderOrders.compareOrthophotoMesh, materialGenerator },
      });
    } else {
      const store = await GeodataStoreFactory.instance.createOrthoTileStore(id);
      const levels: Levels[] = orthotile.data.levels;
      const webMercatorToProject = proj4('EPSG:3857', projectProjection);
      asset = new OrthoTileAsset({
        id: assetId,
        loader: new TileLoader({ store }),
        cache: new TileCache({ maxTextures: 512 }),
        keepRootsVisible: true,
        levels,
        worldMapping: new FlatPlaneWorldMapping({
          tileBoundsToWorldQuad: makeTileBoundsToWorldQuad({
            webMercatorToProject,
            sceneOriginProject: origin, // anchor so numbers stay small
          }),
          anisotropy: 8,
          polygonOffset: { factor: -1, units: -1 },
          transparent: false,
          renderOrder: renderOrders.compareOrthophotoMesh,
          materialGenerator,
        }),
      });
    }

    // Preload roots once to ensure fallback coverage
    asset.preloadRoots();

    return asset;
  }
}
